import { useAuth0 } from '@auth0/auth0-react';
import { useOrganisation } from '@my/directory-api/hooks';
import { cn, ErrorBanner, Loading } from '@my/ui';
import { useEffect, useMemo, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { useWorkViewStore, type WorkViewPage as PageKey } from '@/stores/workView';
import BookingsPage from '../bookings/BookingsPage';
import LegsPage from '../legs/LegsPage';
import ManifestPage from '../manifest/ManifestPage';
import OrdersPage from '../orders/OrdersPage';
import PlanPage from '../plan/PlanPage';
import { enabledWorkViewPages } from './workViewTabs';
import { BookingsIcon, LegsIcon, ManifestIcon, OrdersIcon, PlanIcon } from './workViewIcons';

const ICONS: Record<PageKey, ComponentType> = {
  Orders: OrdersIcon,
  Plan: PlanIcon,
  Manifest: ManifestIcon,
  Legs: LegsIcon,
  Bookings: BookingsIcon,
};

/**
 * The `/orders` container — one route, five swappable pages.
 *
 * <p>
 * WHICH TABS EXIST IS PER-ORGANISATION. The enabled set comes from the
 * Directory org record (`tmsWorkViewTabsJson`, edited by Stratum support on
 * Central Configuration); a missing entry means shown, so an org nobody has
 * configured gets all five. None of the four non-Orders pages has a route of
 * its own, so hiding a tab genuinely removes the screen rather than just its
 * button — there is no URL left to paste.
 * </p>
 *
 * <p>
 * WAITS for that answer rather than guessing, on the same reasoning as
 * `RequireCapability`: rendering all five and then taking two away reads as a
 * bug, and invites a click on the way out. The org query is shared with every
 * other TMS screen and cached for five minutes, so this only ever blocks a
 * genuinely cold load.
 * </p>
 */
export default function WorkViewPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { t } = useTranslation(['configuration', 'common']);

  const organisationQuery = useOrganisation(orgId);
  const currentPage = useWorkViewStore((s) => s.currentPage);
  const setCurrentPage = useWorkViewStore((s) => s.setCurrentPage);

  const pages = useMemo(
    () => enabledWorkViewPages(organisationQuery.data?.tmsWorkViewTabsJson),
    [organisationQuery.data?.tmsWorkViewTabsJson],
  );

  // The store persists the last tab to localStorage, which it validates
  // against the tab set but cannot validate against THIS org's enabled set —
  // it does not know it. So someone parked on Manifest when support switches
  // Manifest off would otherwise land on a page that no longer renders.
  const isCurrentEnabled = pages.includes(currentPage);
  const firstEnabled = pages[0];
  useEffect(() => {
    if (firstEnabled && !isCurrentEnabled) setCurrentPage(firstEnabled);
  }, [firstEnabled, isCurrentEnabled, setCurrentPage]);

  if (organisationQuery.isPending) return <Loading label={t('common:states.loading')} />;

  // Belt and braces. The BE refuses a map with everything off, so this needs a
  // saved row naming tabs that no longer exist — reachable only by renaming a
  // tab in a later build. An explanation beats an empty page.
  if (pages.length === 0) {
    return (
      <div className="p-3">
        <ErrorBanner
          title={t('configuration:workViewTabs.noneEnabledTitle')}
          message={t('configuration:workViewTabs.noneEnabledMessage')}
        />
      </div>
    );
  }

  // `isCurrentEnabled` rather than `currentPage` directly: the effect above
  // corrects the store on the next tick, and rendering the outgoing page for
  // that tick would mount a screen the org is not meant to have. The `??` is
  // unreachable — `pages` is non-empty by the guard above — but the compiler
  // cannot see that through the array index.
  const page = isCurrentEnabled ? currentPage : (firstEnabled ?? currentPage);

  return (
    <div className="flex h-full flex-col p-0">
      <div className="mb-1.5 flex flex-col gap-2 sm:flex-row sm:items-center">
        <WorkViewTabs pages={pages} currentPage={page} onChange={setCurrentPage} />
      </div>

      {page === 'Orders' && <OrdersPage />}
      {page === 'Plan' && <PlanPage />}
      {page === 'Manifest' && <ManifestPage />}
      {page === 'Legs' && <LegsPage />}
      {page === 'Bookings' && <BookingsPage />}
    </div>
  );
}

function WorkViewTabs({
  pages,
  currentPage,
  onChange,
}: {
  pages: readonly PageKey[];
  currentPage: PageKey;
  onChange: (page: PageKey) => void;
}) {
  // A single tab is a label, not a choice. Rendering the strip for it would
  // offer a button that cannot do anything.
  if (pages.length < 2) return null;

  return (
    <div className="flex w-full justify-center rounded-md p-1">
      <div className="rounded-md bg-slate-100 p-1 shadow-sm">
        {pages.map((page) => {
          const isActive = page === currentPage;
          const Icon = ICONS[page];
          return (
            <button
              key={page}
              type="button"
              onClick={() => onChange(page)}
              aria-label={page}
              title={page}
              className={cn(
                'inline-flex h-10 w-14 items-center justify-center rounded-md p-2 transition-colors',
                isActive
                  ? 'bg-white text-slate-600 shadow-sm'
                  : 'text-slate-400 hover:text-slate-900',
              )}
            >
              <Icon />
            </button>
          );
        })}
      </div>
    </div>
  );
}
